import type { DefineFlags } from '@ms-cloudpack/common-types-browser';

function parseArray(value: string): (boolean | string | number)[] | string {
  try {
    const arrayContent = value.substring(1, value.length - 1);
    if (arrayContent.trim() === '') {
      return [];
    }
    const arrayItems = arrayContent.split(',').map((item) => item.trim());
    return arrayItems.map((item) => parseValue(item));
  } catch {
    // If parsing fails, treat as a string
    return value;
  }
}

function isArray(value: string): boolean {
  return value.startsWith('[') && value.endsWith(']');
}

function parseValue(value: string): boolean | string | number {
  // Handle quoted strings (remove the quotes and keep as string)
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.substring(1, value.length - 1);
  }

  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (!isNaN(Number(value)) && value.trim() !== '') {
    return Number(value);
  }
  return value;
}

export function parseDefine(define: string, accDefine: DefineFlags = {}): DefineFlags {
  // Handle empty string case
  if (!define.trim()) {
    return accDefine;
  }

  // Parse define string respecting arrays and quoted strings
  const defineParts: string[] = [];
  let currentPart = '';
  let inArray = 0; // Counter for nested arrays
  let inString: string | null = null; // Track if inside quotes
  let escaped = false; // Track if next character is escaped

  for (const char of define) {
    // Handle escaping characters in strings
    if (inString && char === '\\' && !escaped) {
      escaped = true;
      currentPart += char;
      continue;
    }

    // Handle string boundaries
    if ((char === '"' || char === "'") && !escaped) {
      if (inString === char) {
        inString = null; // Exiting string
      } else if (inString === null) {
        inString = char; // Entering string
      }
      currentPart += char;
      continue;
    }

    if (escaped) escaped = false;

    // Handle array boundaries
    if (char === '[' && !inString) inArray++;
    else if (char === ']' && !inString) inArray--;

    // Split on commas only when not in an array or string
    if (char === ',' && inArray === 0 && inString === null) {
      defineParts.push(currentPart.trim());
      currentPart = '';
    } else {
      currentPart += char;
    }
  }

  // Add the last part
  if (currentPart.trim()) {
    defineParts.push(currentPart.trim());
  }
  for (const part of defineParts) {
    if (!part.trim()) {
      continue;
    }

    // Split on the first equals sign only
    const equalsIndex = part.indexOf('=');
    if (equalsIndex === -1) {
      // No equals sign means boolean flag
      accDefine[part.trim()] = true;
    } else {
      const key = part.substring(0, equalsIndex).trim();
      const valueString = part.substring(equalsIndex + 1).trim();
      accDefine[key] = isArray(valueString) ? parseArray(valueString) : parseValue(valueString);
    }
  }

  return accDefine;
}
